/**
 * Teams OAuth2 / Microsoft 365 API hooks.
 * Endpoints are under /oauth2/teams/* (scoped by channel_id).
 * The generic exchange endpoint is /oauth2/exchange (provider derived from state).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { useToast } from '@vnetwork/ui/feedback';
import { healthcheckApi, hcExtractError, unwrap } from './_shared';

// ---- Types -----------------------------------------------------------------

export interface TeamsOAuthStatus {
  connected: boolean;
  account_label?: string;
  expires_at?: string;
}

export interface TeamsTeam {
  id: string;
  displayName: string;
}

export interface TeamsChannel {
  id: string;
  displayName: string;
}

export interface TeamsChat {
  id: string;
  displayName: string;
}

export interface OAuthExchangeResult {
  connected: boolean;
  account_label?: string;
}

// ---- Query keys ------------------------------------------------------------

export const teamsOAuthKeys = {
  status: (channelId: string) => ['hc', 'teams-oauth', 'status', channelId] as const,
  teams: (channelId: string) => ['hc', 'teams-oauth', 'teams', channelId] as const,
  channels: (channelId: string, teamId: string) =>
    ['hc', 'teams-oauth', 'channels', channelId, teamId] as const,
  chats: (channelId: string) => ['hc', 'teams-oauth', 'chats', channelId] as const,
};

// ---- Queries ---------------------------------------------------------------

export function useTeamsOAuthStatus(channelId: string | undefined) {
  return useQuery<TeamsOAuthStatus>({
    queryKey: teamsOAuthKeys.status(channelId ?? ''),
    enabled: !!channelId,
    staleTime: 10_000,
    queryFn: async () =>
      unwrap<TeamsOAuthStatus>(
        await healthcheckApi.get('/oauth2/teams/status', {
          params: { channel_id: channelId },
        }),
      ),
  });
}

export function useTeamsTeams(channelId: string | undefined) {
  return useQuery<TeamsTeam[]>({
    queryKey: teamsOAuthKeys.teams(channelId ?? ''),
    enabled: !!channelId,
    staleTime: 30_000,
    queryFn: async () => {
      const res = await healthcheckApi.get('/oauth2/teams/teams', {
        params: { channel_id: channelId },
      });
      return normalizeNamedList(res.data);
    },
  });
}

export function useTeamsChannels(channelId: string | undefined, teamId: string | undefined) {
  return useQuery<TeamsChannel[]>({
    queryKey: teamsOAuthKeys.channels(channelId ?? '', teamId ?? ''),
    enabled: !!channelId && !!teamId,
    staleTime: 30_000,
    queryFn: async () => {
      const res = await healthcheckApi.get('/oauth2/teams/channels', {
        params: { channel_id: channelId, team_id: teamId },
      });
      return normalizeNamedList(res.data);
    },
  });
}

export function useTeamsChats(channelId: string | undefined) {
  return useQuery<TeamsChat[]>({
    queryKey: teamsOAuthKeys.chats(channelId ?? ''),
    enabled: !!channelId,
    staleTime: 30_000,
    queryFn: async () => {
      const res = await healthcheckApi.get('/oauth2/teams/chats', {
        params: { channel_id: channelId },
      });
      return normalizeNamedList(res.data);
    },
  });
}

/**
 * Unwraps `{ result | results }` (or a bare array) and maps each item's name to
 * `displayName`, tolerating the BE's snake_case `display_name`. Both /teams and
 * /channels return `{ id, display_name }`, so without this the select labels render
 * blank (FE type uses `displayName`).
 */
function normalizeNamedList<T extends { id: string; displayName: string }>(body: unknown): T[] {
  const raw = Array.isArray(body)
    ? body
    : ((body as { result?: unknown[]; results?: unknown[] })?.result ??
        (body as { results?: unknown[] })?.results ??
        []);
  return (raw as Array<Record<string, unknown>>).map((item) => ({
    ...item,
    id: String(item.id ?? ''),
    displayName: String(item.displayName ?? item.display_name ?? ''),
  })) as T[];
}

// ---- Mutations -------------------------------------------------------------

/** Returns the authorize URL from GET /oauth2/teams/authorize. Not a POST so use plain fn. */
export async function fetchTeamsAuthorizeUrl(channelId: string): Promise<string> {
  const res = await healthcheckApi.get('/oauth2/teams/authorize', {
    params: { channel_id: channelId },
  });
  const body = unwrap<{ authorize_url: string }>(res);
  return body.authorize_url;
}

/** POST /oauth2/exchange — provider-agnostic code exchange. */
export async function exchangeOAuthCode(
  code: string,
  state: string,
): Promise<OAuthExchangeResult> {
  const res = await healthcheckApi.post('/oauth2/exchange', { code, state });
  return unwrap<OAuthExchangeResult>(res);
}

export function useTeamsDisconnect(
  channelId: string,
  options?: UseMutationOptions<void, Error, void>,
) {
  const qc = useQueryClient();
  const { show } = useToast();
  return useMutation<void, Error, void>({
    mutationFn: async () => {
      await healthcheckApi.delete('/oauth2/teams/connection', {
        params: { channel_id: channelId },
      });
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: teamsOAuthKeys.status(channelId) });
      qc.invalidateQueries({ queryKey: teamsOAuthKeys.teams(channelId) });
      // M3: also invalidate channels so a fast reconnect to the same team
      // doesn't serve a stale channels list from the previous connection.
      qc.invalidateQueries({ queryKey: ['hc', 'teams-oauth', 'channels', channelId] });
      show({ tone: 'success', title: 'Microsoft 365 disconnected' });
      options?.onSuccess?.(...args);
    },
    onError: (e, ...rest) => {
      show({ tone: 'danger', title: hcExtractError(e, 'Disconnect failed') });
      options?.onError?.(e, ...rest);
    },
  });
}
